/**
 * PresentationWizard Component
 *
 * Main wizard dialog for the streamlined presentation upload and evaluation flow.
 * Uses the consolidated v2 pipeline for faster evaluation with a single OpenAI call.
 *
 * Flow:
 * 1. Select video file
 * 2. Enter presentation details
 * 3. Upload, evaluate, and view results
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Box,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Wizard step components
import WizardStepUpload from './WizardStepUpload';
import WizardStepDetails from './WizardStepDetails';
import WizardStepEvaluation from './WizardStepEvaluation';

// API utilities
import { uploadPresentation } from '../../../utils/api/presentationApi';
import { executeConsolidatedPipeline, CONSOLIDATED_PIPELINE_STATUS } from '../../../utils/api/consolidatedPipelineApi';

const steps = ['Upload Video', 'Details', 'Evaluate'];

interface PresentationWizardProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export interface WizardFormData {
    description: string;
    productName: string;
    presentationType: string;
    language: string;
    consultantId: string;
    consultantName?: string;
    presenterName?: string;
    evaluationSchemaId?: string;
}

const PresentationWizard: React.FC<PresentationWizardProps> = ({ open, onClose, onSuccess }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [activeStep, setActiveStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [formData, setFormData] = useState<WizardFormData>({
        description: '',
        productName: '',
        presentationType: '',
        language: 'en',
        consultantId: '',
        evaluationSchemaId: ''
    });

    // Upload and evaluation state
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [presentationId, setPresentationId] = useState<string | null>(null);
    const [pipelineStatus, setPipelineStatus] = useState<string | null>(null);
    const [pipelineError, setPipelineError] = useState<string | null>(null);
    const [evaluating, setEvaluating] = useState(false);
    const [isZoomImport, setIsZoomImport] = useState(false);

    // Handle file selection
    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        setIsZoomImport(false);
    };

    // Handle Zoom recording selection
    const handleZoomRecordingSelected = async (importedPresentationId: string, recording: any) => {
        setIsZoomImport(true);
        setPresentationId(importedPresentationId);

        // Populate form data from Zoom metadata
        if (recording) {
            setFormData(prev => ({
                ...prev,
                description: recording.topic || prev.description,
                productName: prev.productName || '',
                presentationType: prev.presentationType || 'presentation'
            }));
        }

        // Skip to evaluation step since presentation is already created
        // Start evaluation pipeline automatically
        try {
            setEvaluating(true);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2);

            const pipelineResult = await executeConsolidatedPipeline(
                importedPresentationId,
                {
                    forceReprocess: false,
                    maxRetries: 3
                },
                (statusUpdate: any) => {
                    console.log('Pipeline status update:', statusUpdate);
                    setPipelineStatus(statusUpdate.status);
                    if (statusUpdate.error) {
                        setPipelineError(statusUpdate.error);
                    }
                }
            );

            setEvaluating(false);

            if (!pipelineResult.success) {
                throw new Error('Evaluation pipeline failed');
            }

            console.log('✅ Evaluation complete!', pipelineResult);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2);

            // Move to evaluation step to show results
            setActiveStep(2);
        } catch (error: any) {
            console.error('❌ Error during evaluation:', error);
            setEvaluating(false);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.FAILED_V2);
            setPipelineError(error.message || 'An unknown error occurred');
            setActiveStep(2); // Still show evaluation step with error
        }
    };

    // Handle form data changes
    const handleFormDataChange = (newData: WizardFormData) => {
        setFormData(newData);
    };

    // Navigate to next step
    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    // Navigate to previous step
    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Handle start evaluation (after details step)
    const handleStartEvaluation = async () => {
        try {
            if (!selectedFile) {
                setPipelineError('Please select a video file first');
                return;
            }

            // Move to evaluation step
            handleNext();

            // Step 1: Upload video file
            console.log('🎬 Step 1: Uploading video file...');
            setUploading(true);
            setPipelineStatus('UPLOADING');

            console.log('📤 DEBUG: Uploading presentation with formData:', {
                ...formData,
                evaluationSchemaId: formData.evaluationSchemaId,
                hasEvaluationSchemaId: !!formData.evaluationSchemaId
            });

            const uploadResult = await uploadPresentation(
                formData,
                selectedFile!,
                (progress: number) => {
                    setUploadProgress(progress);
                }
            );

            if (!uploadResult.success) {
                throw new Error(uploadResult.error || 'Upload failed');
            }

            setUploading(false);
            setPresentationId(uploadResult.presentationId);
            console.log('✅ Video uploaded, presentation ID:', uploadResult.presentationId);

            // Step 2: Execute consolidated v2 pipeline
            console.log('🚀 Step 2: Starting consolidated evaluation pipeline...');
            setEvaluating(true);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.PROCESSING_TRANSCRIPTION_V2);

            const pipelineResult = await executeConsolidatedPipeline(
                uploadResult.presentationId,
                {
                    forceReprocess: false,
                    maxRetries: 3
                },
                (statusUpdate: any) => {
                    // Real-time status updates from pipeline
                    console.log('Pipeline status update:', statusUpdate);
                    setPipelineStatus(statusUpdate.status);
                    if (statusUpdate.error) {
                        setPipelineError(statusUpdate.error);
                    }
                }
            );

            setEvaluating(false);

            if (!pipelineResult.success) {
                throw new Error('Evaluation pipeline failed');
            }

            console.log('✅ Evaluation complete!', pipelineResult);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2);

        } catch (error: any) {
            console.error('❌ Error during upload/evaluation:', error);
            setUploading(false);
            setEvaluating(false);
            setPipelineStatus(CONSOLIDATED_PIPELINE_STATUS.FAILED_V2);
            setPipelineError(error.message || 'An unknown error occurred');
        }
    };

    // Handle view full report
    const handleViewReport = () => {
        if (presentationId) {
            if (onClose) onClose();
            navigate(`/presentations/${presentationId}/report`);
        }
    };

    // Handle retry evaluation
    const handleRetry = async () => {
        setPipelineError(null);
        setPipelineStatus(null);
        setActiveStep(1); // Go back to details step
    };

    // Handle close wizard
    const handleClose = () => {
        // Reset all state
        setActiveStep(0);
        setSelectedFile(null);
        setFormData({
            description: '',
            productName: '',
            presentationType: '',
            language: 'en',
            consultantId: '',
            evaluationSchemaId: ''
        });
        setUploading(false);
        setUploadProgress(0);
        setPresentationId(null);
        setPipelineStatus(null);
        setPipelineError(null);
        setEvaluating(false);

        // Call onSuccess callback if evaluation was successful
        if (onSuccess && pipelineStatus === CONSOLIDATED_PIPELINE_STATUS.COMPLETE_V2) {
            onSuccess();
        }

        // Close dialog
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    minHeight: fullScreen ? '100vh' : '600px'
                }
            }}
        >
            {/* Dialog Header */}
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    New Presentation Evaluation
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                    disabled={uploading || evaluating}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Dialog Content */}
            <DialogContent dividers>
                {/* Stepper */}
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* Step Content */}
                <Box sx={{ minHeight: '400px' }}>
                    {activeStep === 0 && (
                        <WizardStepUpload
                            selectedFile={selectedFile}
                            onFileSelect={handleFileSelect}
                            onNext={handleNext}
                            onZoomRecordingSelected={handleZoomRecordingSelected}
                        />
                    )}

                    {activeStep === 1 && (
                        <WizardStepDetails
                            formData={formData}
                            onFormDataChange={handleFormDataChange}
                            onBack={handleBack}
                            onNext={handleStartEvaluation}
                        />
                    )}

                    {activeStep === 2 && (
                        <WizardStepEvaluation
                            presentationId={presentationId}
                            pipelineStatus={pipelineStatus}
                            pipelineError={pipelineError}
                            onViewReport={handleViewReport}
                            onClose={handleClose}
                            onRetry={handleRetry}
                        />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PresentationWizard;
